import { useEffect, useReducer, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import type { PaletteLike } from "@picnic-field/renderer/appearance";
import { useFieldApi } from "../../../hooks/useFieldApi";
import { usePaletteSet } from "../../../hooks/usePaletteSet";
import { ButtonLink } from "../../../components/ui/Button";
import { PicoBubble } from "../../../components/PicoBubble";
import { ScreenRoot, ToastHost, useToasts } from "../../../components/Shell";
import { StatusScreen } from "../../../components/StatusScreen";
import { useTopBar } from "../../../components/TopBar";
import type { GalleryViewLike, GalleryViewRequest, PrincipleState } from "../../course";
import {
  ROOM_SCENE,
  PrincipleButton,
  PrincipleLabelLayer,
  PrincipleLegend,
  usePrincipleLabels,
} from "../../course";
import { useDensity } from "../../../store/density";
import { useI18n } from "../../../i18n";
import { RemoteOverlay } from "../../remote";
import { useRemote } from "../../remote";
import { ticketOf, useRoomApi } from "../api/roomApi";
import { ownerOf, RoomConnection } from "../lib/connection";
import { roomDeps } from "../lib/deps";
import { Predictor } from "../lib/predict";
import { drivingFromMeta, sumoFromMeta, sumoHeld } from "../lib/modeState";
import { CarryPanel, EmoteBar, SumoPanel, useEmoteBubbles, useRoomCatalog } from "./ModePanel";
import { RelaySide, RelayStageHud, RelayStrip, useRelayStudent } from "./RelayStudent";
import { useCourseApi } from "../../../lib/api/courseApi";
import { useStageModels } from "../../stage";
import {
  arenaFocus,
  arenaOf,
  arenaPlayers,
  chaseFlip,
  chaseFocus,
  chassisHeading,
  followFocus,
  turnFocus,
  winFocus,
} from "../lib/camera";
import { StudentScriptBar } from "./StudentScriptBar";
import { RoomBadge } from "./RoomBadge";
import { isSwapChange } from "../../../lib/api/fieldApi";
import "../../../styles/course.css";
import "../../../styles/room.css";
import type { Focus } from "../types";
import { ZoomControls } from "../../../components/ZoomControls";
import { RotateControls, useViewTurn } from "../../../components/ViewTurn";

declare global {
  interface Window {
    /** 개발·E2E 계측(개발 서버에서만) — 연결 상태 · 서버 틱 추정 · 예측 재조정 수 */
    __pfRoom?: () => {
      state: string;
      serverTick: number | null;
      corrections: number;
      lastInputTick: number;
    };
  }
}

/** 혼자 놀이터로 옮기기까지(SR-LCL-04 전환 ≤10초 — 안내를 읽을 시간만 두고 자동) */
export const TO_LOCAL_MS = 3000;

/** m:ss(룸 타이머 칩) */
const clock = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

/**
 * P2-S1 룸 로비(모드 화면 FS-05~07 이전) — 서버 권위 월드(명부 기계 ≤30)를 스냅샷 보간으로 그리고, 내 기계는 리모컨으로 몬다.
 * 연결 상태(FS-C1): 01 다시 연결 중(20초 카운트다운 · 5초마다) · 03 입장 거부(FE-301/302 — 중립 문구 · 입구로)
 * · 05 혼자 놀이터로(FE-303 → 전시관으로 자동) · 선생님 종료 · 다른 창.
 * 점수·순위·자유 입력 없음(DR-01/02) — 이름은 서버가 준 별명만.
 * P2-S4 선생님 개입(SR-SEC-03 — 중립 문구만, 탓하지 않는다): 일시정지 덮개 · 구경 시간(분리 — 리모컨 숨김) · 룸 타이머 칩
 * · 기계 리셋 알림 · 룸 종료(선생님 · 무활동 — 4000 teacher|idle) → '룸이 끝났어요' + 혼자 놀이터(/course)로 안내(SR-ROOM-04).
 * P2-S5: FS-13-02 단계 표시(상단 바 아래 · 조작 없음) · FS-07-01 자유 놀이터 학생 뷰(조작만 — '배치·삭제는 선생님만' · 물건 n/50)
 * · SR-SOC-04 따라가 보기(친구 이름을 눌러 카메라가 따라감 · 전환 ≤1초 · 보는 동안도 이모트) · 밀어내기 대기자는 지금 경기를 자동 관전.
 * P2-S6: `ev badge`(당사자) → 피코 배지 반응(3초 · 배지 이름만) · `ev swapped`(당사자 · SR-RUN-05) → '새 프로그램으로 바꿨어요' 토스트.
 */
export function RoomScreen() {
  const { t } = useI18n();
  const { density } = useDensity();
  const se = density === "se";
  const navigate = useNavigate();
  const api = useRoomApi();
  const field = useFieldApi();
  const courseApi = useCourseApi();
  const [conn, setConn] = useState<RoomConnection | null>(null);
  const [, rerender] = useReducer((x: number) => x + 1, 0);

  useEffect(() => {
    if (!api) return;
    const d = roomDeps();
    const make = d.createPredictWorld;
    const c = new RoomConnection({
      ticket: () => ticketOf(api),
      connect: d.connect,
      now: d.now,
      // SR-NET-03 — 내 기계 묶음·설정으로 로컬 예측 월드(실패하면 보간만)
      ...(make && courseApi
        ? {
            predictor: async (meta) => {
              const me = meta.machines.find((m) => m.machine === meta.me.machine);
              if (!me) return null;
              const [bundle, runtimeConfig] = await Promise.all([
                courseApi.runBundle(me.snapshotId),
                courseApi.runtimeConfig(),
              ]);
              const world = await make({
                bundle,
                runtimeConfig,
                modeId: meta.mode.id,
                presetKey: meta.mode.presetKey,
                seed: 1,
              });
              return new Predictor(world, d.now);
            },
          }
        : {}),
      setTimeout: (cb, ms) => window.setTimeout(cb, ms),
      clearTimeout: (h) => window.clearTimeout(h as number),
    });
    const off = c.onChange(rerender);
    if (import.meta.env.DEV)
      window.__pfRoom = () => ({
        state: c.state.kind,
        serverTick: c.serverTick(),
        corrections: c.corrections,
        lastInputTick: c.lastInputTick,
      });
    setConn(c);
    c.start();
    return () => {
      off();
      c.dispose();
    };
    // 연결은 룸 API 가 바뀔 때만 새로(코스 API 는 같은 세션)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  // 작품마다 제 appearance.palette 이름의 팔레트(2026-09-30 팔레트 버전 전환)
  const palette = usePaletteSet(
    field,
    conn?.meta?.machines.map((m) => m.appearance),
  );

  const state = conn?.state ?? { kind: "connecting" as const };
  const meta = conn?.meta ?? null;
  const online = (conn?.members ?? []).filter((m) => m.online).length;
  const kind = meta?.mode.kind ?? "lobby";
  const catalog = useRoomCatalog();
  const bubbles = useEmoteBubbles(conn, catalog, roomDeps().now);
  const toasts = useToasts();
  // P3-S2 FS-08-02·03 릴레이(학생) — 상태·조작은 모드와 무관하게 한 번 만든다(훅 순서) · relay 가 아니면 쓰지 않는다
  const relay = useRelayStudent(
    kind === "relay" ? conn : null,
    kind === "relay" ? meta : null,
    (text) => toasts.push("info", text),
  );
  const timerLeft = conn?.timerLeftS() ?? null;
  const paused = !!conn?.paused && state.kind === "live";
  const isolated = !!conn?.isolated;
  const objects = kind === "sandbox" && Array.isArray(meta?.mode.objects) ? meta.mode.objects : [];
  const objectsMax = typeof meta?.mode.max === "number" ? meta.mode.max : 50;
  useTopBar({
    icon:
      kind === "sumo"
        ? "🥊"
        : kind === "carry"
          ? "🤝"
          : kind === "sandbox"
            ? "🧸"
            : kind === "relay"
              ? "🔗"
              : "🏟",
    title: t(
      kind === "sumo"
        ? "room.sumo.title"
        : kind === "carry"
          ? "room.carry.title"
          : kind === "sandbox"
            ? "room.sandbox.title"
            : kind === "relay"
              ? "room.relay.title"
              : "room.title",
    ),
    chips: meta
      ? [
          ...(kind === "sandbox"
            ? [
                {
                  label: t("room.sandbox.objects", { n: objects.length, max: objectsMax }),
                  testId: "room-objects",
                },
              ]
            : []),
          ...(kind === "relay" && relay.view.stages.length
            ? [
                {
                  label:
                    relay.view.phase === "running"
                      ? t("room.relay.chipStage", {
                          n: (relay.view.current ?? 0) + 1,
                          total: relay.view.stages.length,
                        })
                      : t(
                          relay.view.phase === "allDone"
                            ? "room.relay.chipDone"
                            : "room.relay.chipSetup",
                          { total: relay.view.stages.length },
                        ),
                  testId: "room-relay-stage",
                },
              ]
            : []),
          { label: t("room.together", { n: online }), testId: "room-online" },
          ...(timerLeft !== null
            ? [{ label: t("room.timer", { left: clock(timerLeft) }), testId: "room-timer" }]
            : []),
        ]
      : [],
  });

  // 룸 타이머 · 스크립트 단계 — 1초마다 다시 그린다(끝나면 서버가 timerEnd 를 보낸다)
  const timerOn = conn?.timerEndsAt != null || (!!conn?.script && !conn.script.done);
  useEffect(() => {
    if (!timerOn) return;
    const h = window.setInterval(rerender, 1000);
    return () => window.clearInterval(h);
  }, [timerOn]);

  // 기계 리셋 · 타이머 끝 · P2-S6 핫스왑(당사자 — 새 프로그램/조립/색으로 바꿨어요) — 피코 토스트(3초)
  const pushToast = toasts.push;
  useEffect(() => {
    if (!conn) return;
    return conn.onEvent((e) => {
      if (e.t === "machineReset") pushToast("info", t("room.machineReset"));
      else if (e.t === "timerEnd") pushToast("info", t("room.timerEnd"));
      else if (e.t === "swapped")
        pushToast("info", t(`room.swapped.${isSwapChange(e.change) ? e.change : "program"}`));
    });
  }, [conn, pushToast, t]);

  // 밀어내기 승리 연출(2026-10-11) — 이긴 기계 확대 · 🏆 · 색종이(3초)
  const spot = useWinSpotlight(conn);

  // 1초마다 카운트다운 다시 그리기(FS-C1-01)
  useEffect(() => {
    if (state.kind !== "reconnecting") return;
    const h = window.setInterval(rerender, 500);
    return () => window.clearInterval(h);
  }, [state.kind]);

  // FS-C1-05 — 혼자 놀이터(전시관)로 자동
  useEffect(() => {
    if (state.kind !== "failed") return;
    const h = window.setTimeout(() => navigate("/gallery", { replace: true }), TO_LOCAL_MS);
    return () => window.clearTimeout(h);
  }, [state.kind, navigate]);

  const mine = meta?.me.machine ?? null;
  const driving = useDriving(conn, meta);
  // 밀어내기 멈춤 · 3·2·1(2026-09-29) — 리모컨은 보이되 꺼 둔다(흐리게 · 입력 안 보냄) · 카메라 · driving 은 그대로
  const held = useSumoHeld(conn, meta);
  // SR-SOC-04 — 따라가 보는 친구(기계 번호 · null = 나) · 새 월드(FullState)에서 그 기계가 없으면 나로
  const [watch, setWatch] = useState<number | null>(null);
  useEffect(() => {
    if (watch !== null && !meta?.machines.some((m) => m.machine === watch)) setWatch(null);
  }, [meta, watch]);
  const watching = watch !== null && watch !== mine;
  // SR-PHY-07 원리 보기(2026-09-28 룸 화면) — 내 기계만(서버 권위 값 · 켰을 때만 ≈10 Hz) · SE 화살표만 · SU 수치
  const [principleOn, setPrincipleOn] = useState(false);
  useEffect(() => {
    if (!conn || mine === null) return;
    conn.sendPrinciple(principleOn, mine);
  }, [conn, mine, principleOn]);
  const remote = useRemote({
    variant: se ? "se" : "su",
    enabled:
      state.kind === "live" &&
      mine !== null &&
      driving &&
      !held &&
      !isolated &&
      !paused &&
      !watching,
    maxHz: 30,
    send: (s) => conn?.sendRemote(s),
  });

  if (state.kind === "rejected")
    return (
      <ScreenRoot id="FS-ROOM" title={t("room.title")} hideTitle>
        <StatusScreen
          code={state.code}
          audience="student"
          actions={[{ label: t("room.home"), icon: "🏠", onClick: () => navigate("/home") }]}
        />
      </ScreenRoot>
    );
  if (state.kind === "failed")
    return (
      <ScreenRoot id="FS-ROOM" title={t("room.title")} hideTitle>
        <section
          className="pf-pane pf-pane--hero pf-room__notice"
          data-testid="fs-c1-05"
          data-code="FE-303"
        >
          <PicoBubble text={`${t("fe.FE-303.student.cause")} ${t("fe.FE-303.student.next")}`} />
          <ButtonLink to="/gallery" variant="green" icon="🏛" replace>
            {t("room.toLocal")}
          </ButtonLink>
        </section>
      </ScreenRoot>
    );
  // SR-ROOM-04 — 선생님이 끝냈거나 한동안 조용해서 끝났다: 혼자 놀이터(코스)로 이어서
  if (state.kind === "closed" && state.reason)
    return (
      <ScreenRoot id="FS-ROOM" title={t("room.title")} hideTitle>
        <section
          className="pf-pane pf-pane--hero pf-room__notice"
          data-testid="room-ended"
          data-reason={state.reason}
        >
          <h2 className="pf-h">{t("room.ended")}</h2>
          <PicoBubble text={t(state.reason === "idle" ? "room.endedIdle" : "room.endedTeacher")} />
          <div className="pf-row pf-row--wrap">
            <ButtonLink to="/course" variant="green" icon="🏃" replace>
              {t("room.toCourse")}
            </ButtonLink>
            <ButtonLink to="/home" variant="ghost" icon="🏠" replace>
              {t("room.home")}
            </ButtonLink>
          </div>
        </section>
      </ScreenRoot>
    );
  if (state.kind === "closed" || state.kind === "replaced")
    return (
      <ScreenRoot id="FS-ROOM" title={t("room.title")} hideTitle>
        <section
          className="pf-pane pf-pane--hero pf-room__notice"
          data-testid={`room-${state.kind}`}
        >
          <PicoBubble text={t(state.kind === "closed" ? "room.closed" : "room.replaced")} />
          <ButtonLink to="/home" variant="green" icon="🏠" replace>
            {t("room.home")}
          </ButtonLink>
        </section>
      </ScreenRoot>
    );

  const left =
    state.kind === "reconnecting"
      ? Math.max(0, Math.ceil((state.deadline - roomDeps().now()) / 1000))
      : 0;
  return (
    <ScreenRoot id="FS-ROOM" title={t("room.title")} hideTitle fill className="pf-room">
      {!meta ? (
        <section className="pf-pane pf-room__notice" data-testid="room-entering">
          <PicoBubble text={t("room.entering")} busy />
        </section>
      ) : (
        <div
          className={`pf-room__grid${(conn!.script && !conn!.script.done) || kind === "relay" ? " pf-room__grid--bar" : ""}`}
        >
          {kind === "relay" ? (
            <div className="pf-room__topstack">
              <StudentScriptBar script={conn!.script} leftS={conn!.scriptLeftS()} se={se} />
              <RelayStrip view={relay.view} />
            </div>
          ) : (
            <StudentScriptBar script={conn!.script} leftS={conn!.scriptLeftS()} se={se} />
          )}
          <RoomScene
            conn={conn!}
            palette={palette}
            bubbles={bubbles}
            watch={watching ? watch : null}
            autoArena={kind === "sumo" && !driving && !watching}
            spotlight={!watching ? spot : null}
            chase={kind === "sumo" && driving && !watching && mine !== null}
            {...(mine !== null
              ? {
                  principle: { on: principleOn, numbers: !se },
                  principleOwner: ownerOf(mine),
                  onPrinciple: () => setPrincipleOn((o) => !o),
                }
              : {})}
          >
            {kind === "relay" && !watching && <RelayStageHud ctl={relay} conn={conn!} />}
            {spot && !watching && (
              <div
                className={`pf-room__win${spot.mine ? " pf-room__win--mine" : ""}`}
                role="status"
                data-testid="room-win"
                data-mine={spot.mine ? "true" : undefined}
              >
                {spot.mine && (
                  <div className="pf-room__confetti" aria-hidden="true">
                    {CONFETTI.map((c, i) => (
                      <i
                        key={i}
                        style={{ left: `${c.x}%`, animationDelay: `${c.d}s`, background: c.c }}
                      />
                    ))}
                  </div>
                )}
                <b className="pf-room__winstamp">
                  {spot.mine
                    ? t("room.sumo.winMine")
                    : t("room.sumo.winOther", { name: nameOfMachine(conn!, spot.machine) })}
                </b>
              </div>
            )}
            {kind === "sandbox" && (
              <p
                className="pf-room__hud pf-room__hud--right pf-room__hud--dim"
                data-testid="sandbox-teacher-only"
              >
                {t("room.sandbox.teacherOnly")}
              </p>
            )}
            {watching && (
              <div className="pf-room__hud pf-room__hud--left" data-testid="room-watching">
                <span>{t("room.follow.watching", { name: nameOfMachine(conn!, watch) })}</span>
                <button
                  type="button"
                  className="pf-btn pf-btn--ghost pf-btn--sm"
                  onClick={() => setWatch(null)}
                  data-testid="room-watch-back"
                >
                  {t("room.follow.back")}
                </button>
              </div>
            )}
            {conn!.drive !== null && mine !== null && !isolated && !watching && (
              <div
                className="pf-room__hud pf-room__hud--drive"
                role="group"
                aria-label={t("room.drive.label")}
                data-testid="room-drive"
                data-drive={conn!.drive}
              >
                {(["program", "remote"] as const).map((d) => (
                  <button
                    key={d}
                    type="button"
                    className="pf-gallery__hudbtn"
                    aria-pressed={conn!.drive === d}
                    disabled={state.kind !== "live"}
                    data-testid={`room-drive-${d}`}
                    onClick={() => conn!.drive !== d && conn!.sendDrive(d === "remote")}
                  >
                    {t(`room.drive.${d}`)}
                  </button>
                ))}
              </div>
            )}
            {/* 리모컨 모드(밀어내기 · 협동 운반 · 릴레이)가 있는 판에서는 리모컨 모드일 때만 — 프로그램 모드는 프로그램대로 */}
            {mine !== null && driving && !isolated && !watching && conn!.drive !== "program" && (
              <RemoteOverlay
                variant={se ? "se" : "su"}
                remote={remote}
                disabled={state.kind !== "live" || paused || held}
              />
            )}
            {isolated && (
              <p className="pf-room__watch" role="status" data-testid="room-isolated">
                {t("room.isolated")}
              </p>
            )}
            {paused && (
              <div
                className="pf-room__overlay pf-room__paused"
                role="status"
                data-testid="room-paused"
              >
                <PicoBubble text={t("room.paused")} />
              </div>
            )}
          </RoomScene>
          <aside className="pf-pane pf-room__side" aria-label={t("room.members")}>
            {/* 분리 중엔 줄·팀 안내 대신 중립 문구만(SR-SEC-03 — 대기열에서 빠져 있다) */}
            {isolated && (kind === "sumo" || kind === "carry") && (
              <div className="pf-room__mode" data-testid="room-isolated-panel">
                <p className="pf-muted">{t("room.isolatedPanel")}</p>
              </div>
            )}
            {!isolated && kind === "sumo" && (
              <SumoPanel conn={conn!} meta={meta} catalog={catalog} />
            )}
            {!isolated && kind === "carry" && (
              <CarryPanel conn={conn!} meta={meta} catalog={catalog} />
            )}
            {kind === "relay" && <RelaySide ctl={relay} />}
            <EmoteBar conn={conn!} catalog={catalog} phrases={kind !== "lobby"} />
            <h2 className="pf-h3">{t("room.members")}</h2>
            <p className="pf-muted" id="room-follow-hint">
              {t("room.follow.hint")}
            </p>
            <ul
              className="pf-room__members"
              data-testid="room-members"
              aria-describedby="room-follow-hint"
            >
              {conn!.members.map((m) => {
                const me = m.machine !== null && m.machine === mine;
                const can =
                  m.online &&
                  m.machine !== null &&
                  meta.machines.some((x) => x.machine === m.machine);
                const on = me ? !watching : watching && watch === m.machine;
                return (
                  <li
                    key={`${m.machine ?? "x"}-${m.name}`}
                    data-online={m.online}
                    data-me={me ? "true" : undefined}
                  >
                    {can ? (
                      <button
                        type="button"
                        className="pf-room__follow"
                        aria-pressed={on}
                        data-machine={m.machine}
                        onClick={() => setWatch(me ? null : m.machine)}
                      >
                        {me ? t("room.follow.me", { name: m.name }) : m.name}
                        {on && !me && <span aria-hidden="true"> 👀</span>}
                      </button>
                    ) : (
                      <>
                        {m.name}
                        {!m.online && <span className="pf-muted"> · {t("room.offline")}</span>}
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
            {mine === null && (
              <p className="pf-muted" data-testid="room-spectate">
                {t("room.spectate")}
              </p>
            )}
          </aside>
        </div>
      )}
      <ToastHost items={toasts.items} onDismiss={toasts.dismiss} />
      <RoomBadge conn={conn} />
      {state.kind === "reconnecting" && (
        <div className="pf-room__overlay" role="status" data-testid="fs-c1-01">
          <PicoBubble text={t("room.reconnecting", { s: left })} busy />
          <p className="pf-muted">{t("room.reconnectingNext")}</p>
        </div>
      )}
    </ScreenRoot>
  );
}

/**
 * 리모컨을 보일 때 — 로비는 늘 · 밀어내기는 내 경기 중(대기석 기계는 서버가 얼린다) · 협동은 팀이 끝나기 전
 * (FullState 로 시작해 match/result · teamComplete/teamEnd 이벤트로 바뀐다)
 */
/** 밀어내기 승리 연출(2026-10-11) — 이긴 기계 · 내가 이겼나 · 시작 시각(roomDeps().now) */
export interface WinSpot {
  readonly machine: number;
  readonly mine: boolean;
  readonly at: number;
}
/** 연출 길이(ms) — 결과 뒤 대기석으로 옮기기(nextDelayS 3초)와 같다 */
export const WIN_SPOT_MS = 3000;
/** 색종이 조각(자리 % · 늦게 시작 s · 색) — 고정 값(그릴 때마다 같다) */
const CONFETTI = Array.from({ length: 24 }, (_, i) => ({
  x: (i * 37) % 100,
  d: ((i * 13) % 10) / 20,
  c: ["#FFD166", "#EF476F", "#06D6A0", "#118AB2", "#F78C6B"][i % 5]!,
}));

/**
 * 밀어내기 승리 — 내 경기 결과(result.winner · 이겼거나 졌을 때 — 비김 · 무효는 없음)면 이긴 기계를 확대한다.
 * 구경하는 학생은 matchEnd.winner 가 지금 보던 링(첫 경기 링)의 기계일 때만(링이 여럿이어도 화면이 이리저리 튀지 않게).
 */
function useWinSpotlight(conn: RoomConnection | null): WinSpot | null {
  const [spot, setSpot] = useState<WinSpot | null>(null);
  useEffect(() => {
    if (!conn) return;
    let h = 0;
    const show = (machine: number, mine: boolean) => {
      window.clearTimeout(h);
      setSpot({ machine, mine, at: roomDeps().now() });
      h = window.setTimeout(() => setSpot(null), WIN_SPOT_MS);
    };
    const off = conn.onEvent((e) => {
      if (typeof e.winner !== "number") return;
      if (e.t === "result" && (e.outcome === "win" || e.outcome === "lose"))
        show(e.winner, e.outcome === "win");
      else if (e.t === "matchEnd" && conn.meta) {
        const f = conn.frame();
        const live = f ? arenaPlayers(conn.meta, f.transforms)[0] : undefined;
        if (live?.machines.includes(e.winner)) show(e.winner, false);
      }
    });
    return () => {
      off();
      window.clearTimeout(h);
    };
  }, [conn]);
  return spot;
}

function useDriving(conn: RoomConnection | null, meta: RoomConnection["meta"]): boolean {
  // 연결이 이미 받은 match·result(구독 · 이 효과보다 먼저 왔을 수 있다)가 meta 초기값보다 앞선다 — lib/modeState.ts
  const current = () => conn?.modeState.driving ?? drivingFromMeta(meta);
  const [on, setOn] = useState(current);
  // 새 FullState(meta) — 그 뒤 받은 이벤트가 있으면 그것(모드 전환 직후 온 match 를 false 로 덮지 않는다)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setOn(current()), [meta, conn]);
  useEffect(() => {
    if (!conn) return;
    return conn.onEvent((e) => {
      if (e.t === "match") setOn(true);
      else if (e.t === "result" || e.t === "teamComplete" || e.t === "teamEnd") setOn(false);
    });
  }, [conn]);
  return on;
}

/**
 * 밀어내기 내 경기가 멈춤(상대 끊김) · 3·2·1 중인가 — 리모컨을 끈다(서버도 이때 입력을 쓰지 않는다 · SumoCtl.active).
 * 연결이 접어 둔 상태(구독 전 이벤트) → 없으면 FullState meta 에서 · 이후 matchPause/matchResume → 켬 · match/result → 끔
 */
function useSumoHeld(conn: RoomConnection | null, meta: RoomConnection["meta"]): boolean {
  const current = () => {
    if (meta?.mode.kind !== "sumo") return false;
    return sumoHeld(conn?.modeState.sumo ?? sumoFromMeta(meta.mode));
  };
  const [on, setOn] = useState(current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setOn(current()), [meta, conn]);
  useEffect(() => {
    if (!conn) return;
    return conn.onEvent((e) => {
      if (e.t === "matchPause" || e.t === "matchResume") setOn(true);
      else if (e.t === "match" || e.t === "result") setOn(false);
    });
  }, [conn]);
  return on;
}

/** 멤버 이름(기계 번호로) */
function nameOfMachine(conn: RoomConnection, machine: number | null): string {
  return conn.members.find((m) => m.machine === machine)?.name ?? "";
}

/**
 * 3D — 전시관 뷰에 룸 몸체를 싣고, 매 프레임 보간 자세를 넣는다(모든 기계를 관절 모형으로).
 * 교사 콘솔 원리 보기 칸(FS-11-02 💡)도 칠판 연결로 이것을 쓴다(watch = 고른 기계).
 */
export function RoomScene({
  conn,
  palette,
  bubbles,
  watch = null,
  autoArena = false,
  chase = false,
  spotlight = null,
  principle,
  principleOwner,
  onPrinciple,
  children,
}: {
  conn: RoomConnection;
  palette: PaletteLike | null;
  /** 이모트 말풍선(기계 번호 → 2초) */
  bubbles?: ReadonlyMap<number, string>;
  /** SR-SOC-04 따라가 보는 친구 기계(null = 내 기계) */
  watch?: number | null;
  /** 밀어내기 대기 — 지금 경기 링을 본다 */
  autoArena?: boolean;
  /** 밀어내기 내 경기 — 내 기계 뒤 가까이(맵 뷰 단추로 링 전체) */
  chase?: boolean;
  /** 밀어내기 승리 연출 — 이긴 기계를 가까이서 돌며 본다(다른 카메라 규칙보다 앞) */
  spotlight?: WinSpot | null;
  /** SR-PHY-07 원리 보기(내 기계 owner — 없으면 토글 없음) */
  principle?: PrincipleState;
  principleOwner?: string;
  onPrinciple?: () => void;
  children?: ReactNode;
}) {
  const { t } = useI18n();
  const { density } = useDensity();
  const se = density === "se";
  const labels = usePrincipleLabels();
  // 자유 놀이터 교사 꾸밈의 올린 3D 모델(카탈로그 stage_model) — 학생 · 콘솔 원리 보기 칸 모두
  const stageModels = useStageModels(useCourseApi());
  const viewRef = useRef<GalleryViewLike | null>(null);
  const principleRef = useRef<{ on: boolean; numbers: boolean; owners: string[] | null }>({
    on: false,
    numbers: false,
    owners: null,
  });
  const pOn = principle?.on ?? false;
  const pNumbers = principle?.numbers ?? false;
  principleRef.current = {
    on: pOn,
    numbers: pNumbers,
    owners: principleOwner ? [principleOwner] : null,
  };
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const meta = conn.meta!;
  const labelRefs = useRef(new Map<string, HTMLSpanElement>());
  const [view, setView] = useState<"loading" | "3d" | "plan">("loading");
  const [match, setMatch] = useState<string | null>(null);
  // 카메라 규칙은 매 프레임 읽는다(다시 싣지 않고 바뀐다 — 전환 ≤1초는 렌더러 감쇠)
  const focusRef = useRef<(tr: Float32Array) => Focus | null>(() => null);
  // 밀어내기 내 경기 — 맵 뷰(링 전체) 토글 · 모형 앞(±z)은 경기 시작 자리에서 한 번 정한다(경기가 끝나면 다시)
  const [mapView, setMapView] = useState(false);
  const flipRef = useRef<boolean | null>(null);
  // 화면 회전(2026-10-11) — ↺ · ↻ · 끌기 · ⟲. 밀어내기 내 경기 뒤 시점(기계가 보는 쪽)에서는 돌리지 않는다
  const view3 = useViewTurn();
  const turnStep = view3.step;
  const canTurn = !(chase && !mapView) && !spotlight;
  const canTurnRef = useRef(canTurn);
  canTurnRef.current = canTurn;
  if (!chase) flipRef.current = null;
  // 승리 확대 — 처음 방향(이긴 기계 뒤)은 연출이 시작될 때 한 번 정한다
  const spotHeading = useRef<{ at: number; h: number } | null>(null);
  if (!spotlight) spotHeading.current = null;
  focusRef.current = (tr) => {
    if (watch !== null) return followFocus(meta, tr, watch);
    if (spotlight) {
      if (spotHeading.current?.at !== spotlight.at)
        spotHeading.current = {
          at: spotlight.at,
          h: chassisHeading(meta, tr, spotlight.machine) ?? 0,
        };
      const f = winFocus(
        meta,
        tr,
        spotlight.machine,
        Math.max(0, (roomDeps().now() - spotlight.at) / 1000),
        spotHeading.current.h,
      );
      if (f) return f;
    }
    const me = meta.me.machine;
    if (chase && me !== null) {
      const arena = arenaOf(meta, tr, me);
      if (mapView && arena) return arenaFocus(arena);
      if (flipRef.current === null && arena) flipRef.current = chaseFlip(meta, tr, me, arena);
      const f = chaseFocus(meta, tr, me, flipRef.current ?? false);
      if (f) return f;
    }
    if (autoArena) {
      const live = arenaPlayers(meta, tr)[0];
      if (live) return arenaFocus(live.arena);
    }
    return meta.me.machine !== null ? followFocus(meta, tr, meta.me.machine) : null;
  };
  // 밀어내기 대기 중 — '지금 경기: A vs B'(자리로 안다 · 0.5초마다)
  useEffect(() => {
    if (!autoArena) {
      setMatch(null);
      return;
    }
    const names = new Map(conn.members.map((m) => [m.machine, m.name]));
    const h = window.setInterval(() => {
      const f = conn.frame();
      const live = f ? arenaPlayers(meta, f.transforms)[0] : undefined;
      setMatch(live ? live.machines.map((m) => names.get(m) ?? "").join(" vs ") : null);
    }, 500);
    return () => window.clearInterval(h);
  }, [autoArena, conn, meta]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let alive = true;
    let handle: GalleryViewLike | null = null;
    let offPaint: (() => void) | null = null;
    let raf = 0;
    const req: GalleryViewRequest = {
      bodies: meta.bodies as unknown as GalleryViewRequest["bodies"],
      exhibits: meta.machines.map((m) => ({
        owner: m.owner,
        glbUrl: m.glbUrl,
        appearance: m.appearance as GalleryViewRequest["exhibits"][number]["appearance"],
        palette,
      })),
      quality: "low",
      roomScene: ROOM_SCENE,
      // 자유 놀이터 교사 꾸밈(보기 전용 · meta.mode.decor) — 배치가 바뀌면 FullState 로 뷰를 새로 싣는다
      ...(meta.mode.kind === "sandbox" && meta.mode.decor
        ? { roomDecor: meta.mode.decor, stageModelUrl: stageModels.url ?? undefined }
        : {}),
    };
    roomDeps()
      .createGalleryView(canvas, req)
      .then(async (h) => {
        handle = h;
        if (!alive) return h.dispose();
        await h.ready;
        if (!alive) return;
        viewRef.current = h;
        h.setActive(meta.machines.map((m) => m.owner));
        h.setPrinciple?.(principleRef.current);
        offPaint = h.onPaint(() => {
          if (principleRef.current.on) labels.place(h);
        });
        const draw = () => {
          const f = conn.frame();
          if (f) {
            h.update(f);
            // 내 기계(또는 따라가 보는 친구 · 지금 경기)를 따라간다 — 없으면 가운데
            const dh = turnStep(roomDeps().now());
            const v0 =
              focusRef.current(f.transforms) ??
              (canTurnRef.current ? { x: 0, z: 4, back: 9 } : null);
            const v = v0 && canTurnRef.current ? turnFocus(v0, dh) : v0;
            if (v)
              h.setViewpoint(
                { x: v.x, z: v.z },
                v.back || v.heading !== undefined || v.lookUp !== undefined
                  ? {
                      ...(v.back ? { back: v.back } : {}),
                      ...(v.heading !== undefined ? { heading: v.heading } : {}),
                      ...(v.lookUp !== undefined ? { lookUp: v.lookUp } : {}),
                    }
                  : undefined,
              );
            else h.setViewpoint({ x: 0, z: 4 }, { back: 9 });
            for (const m of meta.machines) {
              const el = labelRefs.current.get(m.owner);
              const p = h.project?.(m.bodyStart, 0.25);
              if (el) {
                el.style.display = p ? "" : "none";
                if (p) el.style.transform = `translate(${p.x}px, ${p.y}px)`;
              }
            }
          }
          raf = requestAnimationFrame(draw);
        };
        raf = requestAnimationFrame(draw);
        setView("3d");
      })
      .catch(() => alive && setView("plan"));
    return () => {
      alive = false;
      cancelAnimationFrame(raf);
      offPaint?.();
      viewRef.current = null;
      handle?.dispose();
    };
    // FullState(입장·재접속)마다 새로 싣는다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta, palette, stageModels.url]);

  // 원리 보기 토글 · 밀도(SU 수치) — 뷰에 건다
  useEffect(() => {
    viewRef.current?.setPrinciple?.(principleRef.current);
    if (!pOn) labels.place(null);
    // labels 는 ref 묶음
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pOn, pNumbers, principleOwner]);

  const nameOf = new Map(
    conn.members.filter((m) => m.machine !== null).map((m) => [m.machine!, m]),
  );
  return (
    <div
      className="pf-room__scene"
      data-testid="room-scene"
      data-view={view}
      data-watch={watch ?? undefined}
      data-auto-arena={autoArena || undefined}
      data-principle={pOn ? (pNumbers ? "su" : "se") : "off"}
    >
      <canvas
        ref={canvasRef}
        className="pf-room__canvas"
        data-turn={canTurn ? "on" : "off"}
        {...(canTurn ? view3.canvasHandlers : {})}
      />
      <ZoomControls getView={() => viewRef.current} wheelTarget={canvasRef} />
      {canTurn && view === "3d" && <RotateControls turn={view3} testId="room-rotate" />}
      <PrincipleLabelLayer layer={labels.layer} on={pOn && pNumbers && view === "3d"} />
      {onPrinciple && (
        <div className="pf-room__hud pf-room__hud--principle" data-testid="room-principle">
          <PrincipleButton hud on={pOn} onToggle={onPrinciple} />
          {pOn && <PrincipleLegend se={se} />}
        </div>
      )}
      {chase && (
        <div className="pf-room__hud pf-room__hud--mapview">
          <button
            type="button"
            className="pf-hud pf-gallery__hudbtn"
            aria-pressed={mapView}
            onClick={() => setMapView((o) => !o)}
            data-testid="room-map-view"
          >
            {t(mapView ? "room.follow.behind" : "room.follow.mapView")}
          </button>
        </div>
      )}
      {match && (
        <p className="pf-room__hud pf-room__hud--left" data-testid="room-watch-match">
          {t("room.follow.match", { vs: match })}
        </p>
      )}
      {meta.machines.map((m) => {
        const who = nameOf.get(m.machine);
        if (!who) return null;
        const ports = conn.stalls.get(m.machine);
        return (
          <span
            key={m.owner}
            className="pf-room__label"
            data-me={m.machine === meta.me.machine ? "true" : undefined}
            data-watch={m.machine === watch ? "true" : undefined}
            data-stalled={ports?.length ? "true" : undefined}
            data-online={who.online}
            ref={(el) => {
              if (el) labelRefs.current.set(m.owner, el);
              else labelRefs.current.delete(m.owner);
            }}
          >
            {who.name}
            {ports?.length ? (
              <span className="pf-room__stall">
                {t("room.stalled", { ports: ports.join("·") })}
              </span>
            ) : null}
            {bubbles?.get(m.machine) && (
              <span className="pf-room__bubble" data-testid="emote-bubble">
                {bubbles.get(m.machine)}
              </span>
            )}
          </span>
        );
      })}
      {children}
    </div>
  );
}
